import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, Paper, Chip } from "@mui/material";
import { withRouter } from "react-router";
import { observer } from "mobx-react-lite";
import StyledPage from "../../common/StyledPage";
import StyledSubHeader from "../../common/StyledSubHeader";
import StyledProgressCircle from "../../common/StyledProgressCircle";
import { getSiteContentArticle, updateSiteContent } from "../../../actions/internal/SiteActions";
import { getSiteContentByTrackingId } from "../../../selectors/SiteSelectors";
import { getUserId } from "../../../selectors/UserSelectors";
import ReactMarkdown from 'react-markdown';
import StyledStatusChip from "../../common/StyledStatusChip";
import StyledSection from "../../common/StyledSection";
import { ContainedButton, LoadingButton, TextButton, TooltipIconButton } from "../../common/StyledButtons";
import { GENERATION_STATUS, GENERATION_STATUS_LOOKUP } from "../../../constants/Statuses";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import { SUMMARY_TITLE_FONT } from '../../../constants/Styles';
import ConfirmationModal from "../modals/ConfirmationModal";
import { Edit, Publish, VisibilityOff } from "@mui/icons-material";
import { formatDateToFancyString } from "../../../utilities/DateUtilities";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Showdown from "showdown";
import TurndownService from "turndown";
import Colors from "../../../constants/Colors";
import { StyledMultilineTextField, StyledTextField } from "../../common/StyledTextField";

const EditSiteContentDetailsPage = ({ match, history }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmPublish, setShowConfirmPublish] = useState(false);
  const [title, setTitle] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");


  const siteId = match.params.site_id;
  const trackingId = match.params.article_id;
  const article = getSiteContentByTrackingId(trackingId);

  const modules = {
    toolbar: { container: "#floating-toolbar" }
  };


  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await getSiteContentArticle(getUserId(), siteId, trackingId);
  
      const freshArticle = getSiteContentByTrackingId(trackingId); // pull from store after load
  
      const markdown = freshArticle.content.final_draft.draft_article || "";
  
      // convert markdown to HTML for ReactQuill
      const converter = new Showdown.Converter();
      const html = converter.makeHtml(markdown);

      // EXTRACT FIRST H1
      const h1Match = html.match(/<h1[^>]*>(.*?)<\/h1>/i);
      const extractedTitle = h1Match ? h1Match[1] : "";

      // REMOVE THE FIRST H1 FROM BODY
      const body = html.replace(/<h1[^>]*>.*?<\/h1>/i, "").trim();

      setTitle(extractedTitle);
      setBodyHtml(body);
    } catch (ex) {
      console.error(ex);
    } finally {
      setIsLoading(false);
    }
  };

  const goBackToDetails = () => {
    const siteId = match.params.site_id;
    const docId = match.params.article_id;
  
    history.replace(`/dashboard/site/${siteId}/content/${docId}`);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const finalHtml = `<h1>${title}</h1>\n${bodyHtml}`;

      // Create Turndown instance
      const turndown = new TurndownService({
        headingStyle: "atx",         // # H1
        codeBlockStyle: "fenced",     // ``` code ```
        bulletListMarker: "-",        // cleaner markdown
      });

      // Convert the HTML → Markdown
      const finalMarkdown = turndown.turndown(finalHtml);

      await updateSiteContent(getUserId(), siteId, trackingId, { final_draft: finalMarkdown })
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, "Article has been saved.");
      goBackToDetails();
    } catch (ex) {
      console.error(ex);

    } finally {
      setIsSaving(false);
    }
    


  }

  const renderLoading = () => <StyledProgressCircle />;

  const renderToolbar = () => (
    <Box
      id="floating-toolbar"
      sx={{
        position: "fixed",
        top: 86,
        right: 20,
        zIndex: 3000,
        backgroundColor: "#fff",
        borderRadius: "8px",
        border: "1px solid #ddd",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        padding: "8px",
      }}
    >
      <div class="ql-toolbar ql-snow">

        <span class="ql-formats">
          <button class="ql-bold"></button>
          <button class="ql-italic"></button>
          <button class="ql-underline"></button>
          <button class="ql-strike"></button>
        </span>

        <span class="ql-formats">
          <select class="ql-header">
            <option selected></option>
            <option value="1"></option>
            <option value="2"></option>
            <option value="3"></option>
            <option value="4"></option>
            <option value="5"></option>
            <option value="6"></option>
          </select>
        </span>

        <span class="ql-formats">
          <button class="ql-blockquote"></button>
          <button class="ql-code-block"></button>
        </span>

        <span class="ql-formats">
          <button class="ql-list" value="ordered"></button>
          <button class="ql-list" value="bullet"></button>
        </span>

        <span class="ql-formats">
          <button class="ql-indent" value="-1"></button>
          <button class="ql-indent" value="+1"></button>
        </span>

        <span class="ql-formats">
          <select class="ql-align">
            <option selected></option>
            <option value="center"></option>
            <option value="right"></option>
            <option value="justify"></option>
          </select>
        </span>

        <span class="ql-formats">
          <button class="ql-link"></button>
          <button class="ql-image"></button>
        </span>

        <span class="ql-formats">
          <select class="ql-color"></select>
          <select class="ql-background"></select>
        </span>

        <span class="ql-formats">
          <button class="ql-clean"></button>
        </span>
      </div>
    </Box>
  )

  const renderActionPaper = () => (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 3000,
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        padding: "12px 20px",
        display: "flex",
        gap: "12px",
        alignItems: "center",
      }}
    >
      <TextButton
        onClick={goBackToDetails}
        sx={{ padding: "6px 12px" }}
        disabled={isLoading || isSaving}
      >
        Cancel
      </TextButton>

      <LoadingButton
        color="primary"
        onClick={handleSave}
        isLoading={isSaving}
      >
        Save
      </LoadingButton>

      
    </Box>
  )

  const renderArticleCard = () => (
    <Box>
  <Grid container spacing={1}>
    <Grid item xs={12}>
      <Paper
        variant="outlined"
        sx={{
          marginTop: "70px",
          marginBottom: "80px",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #e0e0e0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
          "&:hover": {
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          },
          transition: "box-shadow 0.2s ease",

          "& .ql-container": {
            border: "none",
          },
          "& .ql-editor": {
            paddingLeft: "10%",
            paddingRight: "10%",
            paddingBottom: "64px",
            minHeight: "300px",
            fontSize: "0.95rem",
            lineHeight: 1.6
          },
          // ACTIVE TEXT (headers, pickers)
          "& .ql-snow .ql-picker.ql-active .ql-picker-label": {
            color: `${Colors.primaryColor} !important`,
          },
          "& .ql-snow .ql-picker-label.ql-active": {
            color: `${Colors.primaryColor} !important`,
          },
          "& .ql-snow .ql-picker-item.ql-selected": {
            color: `${Colors.primaryColor} !important`,
          },

          // ACTIVE ICONS (bold/italic/underline/etc.)
          "& .ql-snow .ql-toolbar button.ql-active .ql-stroke": {
            stroke: `${Colors.primaryColor} !important`,
          },
          "& .ql-snow .ql-toolbar button.ql-active .ql-fill": {
            fill: `${Colors.primaryColor} !important`,
          },

          // HOVER STATE (optional)
          "& .ql-snow .ql-toolbar button:hover .ql-stroke": {
            stroke: `${Colors.primaryColor}`,
          },
          "& .ql-snow .ql-toolbar button:hover .ql-fill": {
            fill: `${Colors.primaryColor}`,
          },

            /*** PICKER LABEL (align, color, background, header) ***/
          "& .ql-snow .ql-picker-label:hover": {
            color: `${Colors.primaryColor} !important`,
          },

          /*** CARET ICON (the small arrow) ***/
          "& .ql-snow .ql-picker-label:hover .ql-stroke": {
            stroke: `${Colors.primaryColor} !important`,
          },

          /*** DROPDOWN PANEL BACKGROUND ***/
          "& .ql-snow .ql-picker-options": {
            backgroundColor: "#fff !important",
            borderColor: `${Colors.primaryColor} !important`,
          },

          /*** DROPDOWN ITEM — ACTIVE ***/
          "& .ql-snow .ql-picker-item.ql-selected": {
            color: `${Colors.primaryColor} !important`,
          },

          /*** DROPDOWN ITEM — HOVER ***/
          "& .ql-snow .ql-picker-item:hover": {
            color: "#fff !important",
            backgroundColor: `${Colors.primaryColor} !important`,
          },

          /*** ALIGN PICKER ICONS (they use strokes too) ***/
          "& .ql-snow .ql-picker-item:hover .ql-stroke": {
            stroke: "#fff !important",
          },
          "& .ql-snow .ql-picker-item.ql-selected .ql-stroke": {
            stroke: `${Colors.primaryColor} !important`,
          },
          
        }}
      >
        <Box sx={{
          marginTop: "64px",
          paddingLeft: "10%",
          paddingRight: "10%",
          marginBottom: "18px",
        }}>
          <StyledTextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter article title..."
            multiline
            maxRows={3}     // auto-expands only if needed
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: "2rem",         // title size
                fontWeight: 700,
                padding: 0,
                lineHeight: 1.2,
              }
            }}
            sx={{
              
              borderRadius: 0,
              "& .MuiOutlinedInput-root": {
                padding: 0,
                "& fieldset": { border: "none" },            // remove outline box
                "&:before, &:after": { border: "none" },     // remove MUI underline defaults
                borderBottom: `2px solid ${Colors.primaryColor}`,
              },

              // Add hover/focus color
              "& .MuiOutlinedInput-root:hover": {
                borderBottomColor: Colors.primaryColor,
              },
              "& .Mui-focused": {
                borderBottomColor: Colors.primaryColor,
              },

              // Title text color
              "& input, & textarea": {
                fontSize: "2rem",
                fontWeight: 700,
                padding: "8px 0",
                lineHeight: 1.2,
              }
            }}
          />
        </Box>
        
        <ReactQuill 
          value={bodyHtml} 
          onChange={setBodyHtml}
          modules={modules}
          sx={{
                '& h1': { fontSize: '1.9rem', fontWeight: 600, mt: 2, mb: 1 },
                '& h2': { fontSize: '1.4rem', fontWeight: 600, mt: 2, mb: 1, textTransform: 'none' },
                '& h3': { fontSize: '1.15rem', fontWeight: 600, mt: 1.5, mb: 0.8, textTransform: 'none' },
                '& h4': { fontSize: '1rem', fontWeight: 500, mt: 1.2, mb: 0.6, textTransform: 'none' },
                '& h5': { fontSize: '0.9rem', fontWeight: 500, mt: 1, mb: 0.5, textTransform: 'none' },
                '& p': { fontSize: '0.95rem', lineHeight: 1.6, mb: 1, textTransform: 'none' },
                '& ul, & ol': { fontSize: '0.95rem', ml: 3 },
                '& code': {
                fontSize: '0.85rem',
                backgroundColor: '#f5f5f5',
                px: 0.5,
                py: 0.2,
                borderRadius: '4px',
                fontFamily: 'monospace',
                }
            }}/>
            {renderToolbar()}
      </Paper>
      {renderActionPaper()}
       

    </Grid>
  </Grid>
</Box>
  );

  return (
    <StyledPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {isLoading ? renderLoading() : 
          <>
            {renderArticleCard()}
          </>
          
          
          }
        </Grid>
      </Grid>
    </StyledPage>
  );
};

export default observer(withRouter(EditSiteContentDetailsPage));
